import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type { MotionCommandReason, Velocity } from './teleopMotionTypes';

type PointerSurfaceRef = RefObject<HTMLDivElement | null>;

type MotionKnobOffset = {
  x: number;
  y: number;
};

type MovementKeyState = {
  left: boolean;
  right: boolean;
  up: boolean;
  down: boolean;
};

type RotationKeyState = {
  rotateLeft: boolean;
  rotateRight: boolean;
};

type MovementDirection = keyof MovementKeyState;
type RotationDirection = keyof RotationKeyState;

type UseTeleopMotionInputParams = {
  joystickRef: PointerSurfaceRef;
  zAreaRef: PointerSurfaceRef;
  maxDist: number;
  keyRepeatMs: number;
  keyboardRotationVelocity: number;
  updateDesiredMotion: (
    partial: Partial<Velocity>,
    reason: MotionCommandReason,
  ) => void;
  triggerGrab: () => void;
  canTriggerGrabWithKeyboard: boolean;
  bonusPromptVisible: boolean;
  resultPromptVisible: boolean;
  acknowledgeBonusAttemptPrompt: () => void;
  handlePlayAgain: () => void;
};

type UseTeleopMotionInputResult = {
  knobOffset: MotionKnobOffset;
  zAngle: number;
};

const hasActiveMovementKeys = (state: MovementKeyState) => {
  const hasActiveMovement =
    state.left ||
    state.right ||
    state.up ||
    state.down;

  return hasActiveMovement;
};

const hasActiveRotationKeys = (state: RotationKeyState) => {
  const hasActiveRotation =
    state.rotateLeft ||
    state.rotateRight;

  return hasActiveRotation;
};

const getNormalizedKey = (event: KeyboardEvent) => {
  const isSingleCharacterKey = event.key.length === 1;

  if (isSingleCharacterKey) {
    const normalizedKey = event.key.toLowerCase();

    return normalizedKey;
  }

  return event.key;
};

const getMovementDirectionForKey = (
  key: string,
): MovementDirection | null => {
  if (key === 'ArrowLeft') {
    return 'left';
  }

  if (key === 'ArrowRight') {
    return 'right';
  }

  if (key === 'ArrowUp') {
    return 'up';
  }

  if (key === 'ArrowDown') {
    return 'down';
  }

  return null;
};

const getRotationDirectionForCode = (
  code: string,
): RotationDirection | null => {
  if (code === 'KeyA') {
    return 'rotateLeft';
  }

  if (code === 'KeyD' || code === 'KeyS') {
    return 'rotateRight';
  }

  return null;
};

export const useTeleopMotionInput = ({
  joystickRef,
  zAreaRef,
  maxDist,
  keyRepeatMs,
  keyboardRotationVelocity,
  updateDesiredMotion,
  triggerGrab,
  canTriggerGrabWithKeyboard,
  bonusPromptVisible,
  resultPromptVisible,
  acknowledgeBonusAttemptPrompt,
  handlePlayAgain,
}: UseTeleopMotionInputParams): UseTeleopMotionInputResult => {
  const [knobOffset, setKnobOffset] = useState<MotionKnobOffset>({
    x: 0,
    y: 0,
  });
  const [zAngle, setZAngle] = useState(0);
  const joystickActiveRef = useRef(false);
  const zActiveRef = useRef(false);
  const keyRepeatRef = useRef<number | null>(null);
  const spaceKeyActiveRef = useRef(false);
  const movementKeyStateRef = useRef<MovementKeyState>({
    left: false,
    right: false,
    up: false,
    down: false,
  });
  const rotationKeyStateRef = useRef<RotationKeyState>({
    rotateLeft: false,
    rotateRight: false,
  });

  const applyMovementKeyState = useCallback(() => {
    const joystickIsActive = joystickActiveRef.current;

    if (joystickIsActive) {
      return;
    }

    const movementState = movementKeyStateRef.current;
    const xAxis = (movementState.right ? 1 : 0) - (movementState.left ? 1 : 0);
    const yAxis = (movementState.up ? 1 : 0) - (movementState.down ? 1 : 0);
    const hasNoMovement = xAxis === 0 && yAxis === 0;

    if (hasNoMovement) {
      const centeredKnobOffset = {
        x: 0,
        y: 0,
      };

      setKnobOffset(centeredKnobOffset);
      updateDesiredMotion({ x: 0, y: 0 }, 'movement_key_release');

      return;
    }

    const movementMagnitude = Math.hypot(xAxis, yAxis);
    const normalizedX = movementMagnitude > 1 ? xAxis / movementMagnitude : xAxis;
    const normalizedY = movementMagnitude > 1 ? yAxis / movementMagnitude : yAxis;
    const nextKnobOffset = {
      x: normalizedX * maxDist,
      y: normalizedY * -maxDist,
    };
    const nextVelocity = {
      x: Number(normalizedX.toFixed(2)),
      y: Number(normalizedY.toFixed(2)),
    };

    setKnobOffset(nextKnobOffset);
    updateDesiredMotion(nextVelocity, 'movement_key_update');
  }, [maxDist, updateDesiredMotion]);

  const applyRotationKeyState = useCallback(() => {
    const zControlIsActive = zActiveRef.current;

    if (zControlIsActive) {
      return;
    }

    const rotationState = rotationKeyStateRef.current;
    const zAxis =
      (rotationState.rotateRight ? 1 : 0) -
      (rotationState.rotateLeft ? 1 : 0);
    const hasNoRotation = zAxis === 0;

    if (hasNoRotation) {
      setZAngle(0);
      updateDesiredMotion({ z: 0 }, 'rotation_key_release');

      return;
    }

    const nextVelocity = Number((zAxis * keyboardRotationVelocity).toFixed(2));
    const nextAngle = nextVelocity * 180;

    setZAngle(nextAngle);
    updateDesiredMotion({ z: nextVelocity }, 'rotation_key_update');
  }, [keyboardRotationVelocity, updateDesiredMotion]);

  const applyKeyboardState = useCallback(() => {
    applyMovementKeyState();
    applyRotationKeyState();
  }, [applyMovementKeyState, applyRotationKeyState]);

  const isEditableTarget = useCallback((target: EventTarget | null) => {
    const isHtmlElement = target instanceof HTMLElement;

    if (!isHtmlElement) {
      return false;
    }

    if (target.isContentEditable) {
      return true;
    }

    const tagName = target.tagName.toLowerCase();
    const isEditableTag =
      tagName === 'input' ||
      tagName === 'textarea' ||
      tagName === 'select';

    return isEditableTag;
  }, []);

  const startKeyRepeat = useCallback(() => {
    const hasKeyRepeatInterval = keyRepeatRef.current !== null;

    if (hasKeyRepeatInterval) {
      return;
    }

    keyRepeatRef.current = window.setInterval(() => {
      applyKeyboardState();
    }, keyRepeatMs);
  }, [applyKeyboardState, keyRepeatMs]);

  const stopKeyRepeat = useCallback(() => {
    const keyRepeatIntervalId = keyRepeatRef.current;
    const hasKeyRepeatInterval = keyRepeatIntervalId !== null;

    if (!hasKeyRepeatInterval) {
      return;
    }

    window.clearInterval(keyRepeatIntervalId);
    keyRepeatRef.current = null;
  }, []);

  useEffect(() => {
    const updateMovementKey = (
      direction: MovementDirection,
      isDown: boolean,
    ) => {
      const currentState = movementKeyStateRef.current;
      const didStateChange = currentState[direction] !== isDown;

      if (!didStateChange) {
        return false;
      }

      const nextState = {
        ...currentState,
        [direction]: isDown,
      };

      movementKeyStateRef.current = nextState;

      return true;
    };

    const updateRotationKey = (
      direction: RotationDirection,
      isDown: boolean,
    ) => {
      const currentState = rotationKeyStateRef.current;
      const didStateChange = currentState[direction] !== isDown;

      if (!didStateChange) {
        return false;
      }

      const nextState = {
        ...currentState,
        [direction]: isDown,
      };

      rotationKeyStateRef.current = nextState;

      return true;
    };

    const onKeyDown = (event: KeyboardEvent) => {
      const targetIsEditable = isEditableTarget(event.target);
      const isSpaceKey = event.code === 'Space';

      if (isSpaceKey) {
        if (targetIsEditable) {
          return;
        }

        const hasModifierKey =
          event.ctrlKey ||
          event.metaKey ||
          event.altKey;

        if (hasModifierKey) {
          return;
        }

        event.preventDefault();
        const wasRepeated = event.repeat;
        const spaceKeyIsAlreadyActive = spaceKeyActiveRef.current;

        if (wasRepeated || spaceKeyIsAlreadyActive) {
          return;
        }

        const promptIsVisible =
          bonusPromptVisible ||
          resultPromptVisible;

        if (promptIsVisible) {
          spaceKeyActiveRef.current = true;

          if (bonusPromptVisible) {
            acknowledgeBonusAttemptPrompt();

            return;
          }

          handlePlayAgain();

          return;
        }

        if (!canTriggerGrabWithKeyboard) {
          return;
        }

        spaceKeyActiveRef.current = true;
        triggerGrab();

        return;
      }

      if (targetIsEditable) {
        return;
      }

      const hasModifierKey =
        event.ctrlKey ||
        event.metaKey ||
        event.altKey;

      if (hasModifierKey) {
        return;
      }

      const normalizedKey = getNormalizedKey(event);
      const movementDirection = getMovementDirectionForKey(normalizedKey);
      const rotationDirection = getRotationDirectionForCode(event.code);
      const shouldHandleMovement = movementDirection !== null;
      const shouldHandleRotation = rotationDirection !== null;
      const shouldHandleInput =
        shouldHandleMovement ||
        shouldHandleRotation;

      if (!shouldHandleInput) {
        return;
      }

      event.preventDefault();
      let didUpdateKey = false;

      if (movementDirection && updateMovementKey(movementDirection, true)) {
        didUpdateKey = true;
      }

      if (rotationDirection && updateRotationKey(rotationDirection, true)) {
        didUpdateKey = true;
      }

      if (!didUpdateKey) {
        return;
      }

      startKeyRepeat();
      applyKeyboardState();
    };

    const onKeyUp = (event: KeyboardEvent) => {
      const targetIsEditable = isEditableTarget(event.target);
      const isSpaceKey = event.code === 'Space';

      if (isSpaceKey) {
        if (targetIsEditable) {
          return;
        }

        event.preventDefault();
        spaceKeyActiveRef.current = false;

        return;
      }

      if (targetIsEditable) {
        return;
      }

      const normalizedKey = getNormalizedKey(event);
      const movementDirection = getMovementDirectionForKey(normalizedKey);
      const rotationDirection = getRotationDirectionForCode(event.code);
      const shouldHandleMovement = movementDirection !== null;
      const shouldHandleRotation = rotationDirection !== null;
      const shouldHandleInput =
        shouldHandleMovement ||
        shouldHandleRotation;

      if (!shouldHandleInput) {
        return;
      }

      event.preventDefault();
      let didUpdateKey = false;

      if (movementDirection && updateMovementKey(movementDirection, false)) {
        didUpdateKey = true;
      }

      if (rotationDirection && updateRotationKey(rotationDirection, false)) {
        didUpdateKey = true;
      }

      if (!didUpdateKey) {
        return;
      }

      applyKeyboardState();
      const movementState = movementKeyStateRef.current;
      const rotationState = rotationKeyStateRef.current;
      const movementIsActive = hasActiveMovementKeys(movementState);
      const rotationIsActive = hasActiveRotationKeys(rotationState);
      const shouldStopKeyRepeat =
        !movementIsActive &&
        !rotationIsActive;

      if (shouldStopKeyRepeat) {
        stopKeyRepeat();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      stopKeyRepeat();
    };
  }, [
    acknowledgeBonusAttemptPrompt,
    applyKeyboardState,
    bonusPromptVisible,
    canTriggerGrabWithKeyboard,
    handlePlayAgain,
    isEditableTarget,
    resultPromptVisible,
    startKeyRepeat,
    stopKeyRepeat,
    triggerGrab,
  ]);

  const updateJoystick = useCallback(
    (clientX: number, clientY: number) => {
      const joystickElement = joystickRef.current;

      if (!joystickElement) {
        return;
      }

      const rect = joystickElement.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      let deltaX = clientX - centerX;
      let deltaY = clientY - centerY;
      const distanceFromCenter = Math.hypot(deltaX, deltaY);
      const exceedsMaxDistance = distanceFromCenter > maxDist;

      if (exceedsMaxDistance) {
        const distanceScale = maxDist / distanceFromCenter;

        deltaX *= distanceScale;
        deltaY *= distanceScale;
      }

      const nextKnobOffset = {
        x: deltaX,
        y: deltaY,
      };
      const nextVelocity = {
        x: Number((deltaX / maxDist).toFixed(2)),
        y: Number(((deltaY / maxDist) * -1).toFixed(2)),
      };

      setKnobOffset(nextKnobOffset);
      updateDesiredMotion(nextVelocity, 'joystick_update');
    },
    [joystickRef, maxDist, updateDesiredMotion]
  );

  const updateRotationWheel = useCallback(
    (clientX: number, clientY: number) => {
      const zAreaElement = zAreaRef.current;

      if (!zAreaElement) {
        return;
      }

      const rect = zAreaElement.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const deltaX = clientX - centerX;
      const deltaY = clientY - centerY;
      const isCenteredPoint = deltaX === 0 && deltaY === 0;

      if (isCenteredPoint) {
        return;
      }

      const rawAngle = (Math.atan2(deltaY, deltaX) * 180) / Math.PI;
      let rotationAngle = rawAngle + 90;
      const exceedsPositiveRange = rotationAngle > 180;
      const exceedsNegativeRange = rotationAngle < -180;

      if (exceedsPositiveRange) {
        rotationAngle -= 360;
      }

      if (exceedsNegativeRange) {
        rotationAngle += 360;
      }

      const clampedVelocity = Math.max(-1, Math.min(1, rotationAngle / 180));
      const nextVelocity = Number(clampedVelocity.toFixed(2));

      setZAngle(rotationAngle);
      updateDesiredMotion({ z: nextVelocity }, 'rotation_dial_update');
    },
    [updateDesiredMotion, zAreaRef]
  );

  useEffect(() => {
    const joystickElement = joystickRef.current;

    if (!joystickElement) {
      return;
    }

    let activePointerId: number | null = null;

    const onPointerDown = (event: PointerEvent) => {
      const alreadyTrackingPointer = activePointerId !== null;

      if (alreadyTrackingPointer) {
        return;
      }

      joystickActiveRef.current = true;
      activePointerId = event.pointerId;
      joystickElement.setPointerCapture(event.pointerId);
      updateJoystick(event.clientX, event.clientY);
    };

    const onPointerMove = (event: PointerEvent) => {
      const isActivePointer = event.pointerId === activePointerId;

      if (!isActivePointer) {
        return;
      }

      updateJoystick(event.clientX, event.clientY);
    };

    const onPointerUp = (event: PointerEvent) => {
      const isActivePointer = event.pointerId === activePointerId;

      if (!isActivePointer) {
        return;
      }

      joystickActiveRef.current = false;
      activePointerId = null;
      setKnobOffset({ x: 0, y: 0 });
      updateDesiredMotion({ x: 0, y: 0 }, 'joystick_release');
      const movementState = movementKeyStateRef.current;
      const movementIsActive = hasActiveMovementKeys(movementState);

      if (movementIsActive) {
        applyMovementKeyState();

        return;
      }

      const rotationState = rotationKeyStateRef.current;
      const rotationIsActive = hasActiveRotationKeys(rotationState);

      if (rotationIsActive) {
        applyRotationKeyState();
      }
    };

    joystickElement.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);

    return () => {
      joystickElement.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
    };
  }, [
    applyMovementKeyState,
    applyRotationKeyState,
    joystickRef,
    updateDesiredMotion,
    updateJoystick,
  ]);

  useEffect(() => {
    const zAreaElement = zAreaRef.current;

    if (!zAreaElement) {
      return;
    }

    let activePointerId: number | null = null;

    const onPointerDown = (event: PointerEvent) => {
      const alreadyTrackingPointer = activePointerId !== null;

      if (alreadyTrackingPointer) {
        return;
      }

      zActiveRef.current = true;
      activePointerId = event.pointerId;
      zAreaElement.setPointerCapture(event.pointerId);
      updateRotationWheel(event.clientX, event.clientY);
    };

    const onPointerMove = (event: PointerEvent) => {
      const isActivePointer = event.pointerId === activePointerId;

      if (!isActivePointer) {
        return;
      }

      updateRotationWheel(event.clientX, event.clientY);
    };

    const onPointerUp = (event: PointerEvent) => {
      const isActivePointer = event.pointerId === activePointerId;

      if (!isActivePointer) {
        return;
      }

      zActiveRef.current = false;
      activePointerId = null;
      setZAngle(0);
      updateDesiredMotion({ z: 0 }, 'rotation_dial_release');
      const movementState = movementKeyStateRef.current;
      const movementIsActive = hasActiveMovementKeys(movementState);

      if (movementIsActive) {
        applyMovementKeyState();

        return;
      }

      const rotationState = rotationKeyStateRef.current;
      const rotationIsActive = hasActiveRotationKeys(rotationState);

      if (rotationIsActive) {
        applyRotationKeyState();
      }
    };

    zAreaElement.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);

    return () => {
      zAreaElement.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
    };
  }, [
    applyMovementKeyState,
    applyRotationKeyState,
    updateDesiredMotion,
    updateRotationWheel,
    zAreaRef,
  ]);

  return {
    knobOffset,
    zAngle,
  };
};
